import { useEffect, useRef, useState } from "react";

/**
 * useNativeFullscreen
 *
 * Provides native OS-level fullscreen for any element.
 * - attach `ref` to the element you want to fullscreen, OR pass an existing ref via `externalRef`
 * - call `enterFullscreen()` to go fullscreen
 * - `isFullscreen` tracks current state (synced via fullscreenchange event)
 *
 * Falls back to `document.documentElement` if the element ref is unavailable
 * or if the browser rejects the element-level request (e.g. permission policy).
 *
 * @param {React.RefObject} [externalRef] - Optional existing ref to use instead of creating a new one.
 *   Useful when the component already has a containerRef and you don't want an extra wrapper div.
 */
const useNativeFullscreen = (externalRef = null) => {
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const enterFullscreen = () => {
    if (isFullscreen) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      return;
    }
    const el = ref.current || document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      req.call(el).catch(() => {
        // Element blocked — fall back to full page
        document.documentElement.requestFullscreen?.().catch(console.error);
      });
    }
  };

  return { ref, isFullscreen, enterFullscreen };
};

export default useNativeFullscreen;
